import { useRef, useState } from 'react'
import { Upload, Paperclip, X } from 'lucide-react'
import { Button } from '../../lib/shadcn/button'
import { Label } from '../../lib/shadcn/label'
import { Spinner } from '../../lib/shadcn/spinner'
import { Textarea } from '../../lib/shadcn/textarea'
import { toast } from '../../lib/shadcn/sonner'
import { useUploadDocument } from '../../hooks/backend/documents'
import { writeErrorMessage } from '../../utils/writeError'

const MAX_BYTES = 15 * 1024 * 1024
const ACCEPT = 'image/*,application/pdf'

export type UploadedEvidence = { document_id: string; title: string; drive: boolean }

function readAsBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => {
      const result = typeof reader.result === 'string' ? reader.result : ''
      resolve(result.split(',')[1] ?? '')
    }
    reader.onerror = () => reject(reader.error ?? new Error('Could not read the file.'))
    reader.readAsDataURL(file)
  })
}

/**
 * Evidence for an incident: photographs and PDFs, plus links for anything
 * already hosted elsewhere.
 *
 * The photographs of an accident are on somebody's phone, not at a URL, which is
 * why a link box alone was the wrong shape. Files go through the same documents
 * path as every other upload in the cockpit, into the vehicle's folder and
 * Supabase Storage, tagged as incident evidence, and are claimed by the incident
 * once it is saved.
 *
 * The link box stays for the cases where the evidence really does already live
 * somewhere, an assessor's portal or a shared album.
 *
 * Uploading needs the vehicle, because that is what the document is filed
 * against. Until one is chosen the control says so rather than failing later.
 */
export function IncidentEvidence({
  vehicleId,
  uploaded,
  onUploaded,
  onRemove,
  links,
  onLinksChange,
}: {
  vehicleId: string | null
  uploaded: UploadedEvidence[]
  onUploaded: (next: UploadedEvidence) => void
  onRemove: (documentId: string) => void
  links: string
  onLinksChange: (v: string) => void
}) {
  const upload = useUploadDocument()
  const inputRef = useRef<HTMLInputElement>(null)
  const [pending, setPending] = useState<File[]>([])
  const [tooLarge, setTooLarge] = useState<string | null>(null)

  function handleFiles(e: React.ChangeEvent<HTMLInputElement>) {
    const chosen = Array.from(e.target.files ?? [])
    const over = chosen.find((f) => f.size > MAX_BYTES)
    setTooLarge(over ? over.name : null)
    setPending(over ? [] : chosen)
  }

  async function handleUpload() {
    if (!vehicleId || pending.length === 0) return
    let failed = 0
    for (const file of pending) {
      try {
        const contentBase64 = await readAsBase64(file)
        const res = (await upload.trigger({
          vehicle_id: vehicleId,
          owner_person_id: null,
          category: 'incident_evidence',
          filename: file.name,
          contentType: file.type || 'application/octet-stream',
          contentBase64,
        }).result) as { document?: { id?: string; title?: string; drive_url?: string | null } } | null
        const id = res?.document?.id
        if (!id) throw new Error('The upload did not come back with a document.')
        onUploaded({
          document_id: id,
          title: res?.document?.title ?? file.name,
          drive: !!res?.document?.drive_url,
        })
      } catch (err) {
        failed++
        toast.error(`Could not upload ${file.name}`, { description: writeErrorMessage(err) })
      }
    }
    setPending([])
    if (inputRef.current) inputRef.current.value = ''
    if (failed === 0) toast.success(pending.length === 1 ? 'File attached' : 'Files attached')
  }

  return (
    <div className="space-y-2 rounded-md border border-border bg-muted/30 p-3">
      <Label className="text-xs uppercase tracking-wide text-muted-foreground">
        Evidence (optional)
      </Label>

      <div className="flex flex-col gap-2 sm:flex-row sm:items-end">
        <div className="min-w-0 flex-1 space-y-1.5">
          <Label htmlFor="inc-files" className="text-xs">
            Photographs or PDFs
          </Label>
          <input
            ref={inputRef}
            id="inc-files"
            type="file"
            multiple
            accept={ACCEPT}
            disabled={!vehicleId || upload.loading}
            onChange={handleFiles}
            className="flex h-9 w-full rounded-md border border-input bg-background text-sm text-foreground file:mr-3 file:h-full file:border-0 file:bg-muted file:px-3 file:text-sm file:font-medium file:text-foreground disabled:cursor-not-allowed disabled:opacity-50"
          />
        </div>
        <Button
          type="button"
          onClick={handleUpload}
          disabled={!vehicleId || pending.length === 0 || upload.loading}
          size="sm"
          className="h-9 shrink-0 gap-1.5"
        >
          {upload.loading ? <Spinner className="h-4 w-4" /> : <Upload className="h-4 w-4" />}
          Attach
        </Button>
      </div>

      {!vehicleId && (
        <p className="text-[11px] text-muted-foreground">
          Choose a vehicle first. Evidence is filed against the vehicle it belongs to.
        </p>
      )}
      {tooLarge && (
        <p className="text-xs text-destructive">
          {tooLarge} is larger than 15 MB. Please choose a smaller file.
        </p>
      )}

      {uploaded.length > 0 && (
        <ul className="space-y-1">
          {uploaded.map((f) => (
            <li
              key={f.document_id}
              className="flex items-center gap-2 rounded border border-border bg-background px-2 py-1 text-xs"
            >
              <Paperclip className="h-3 w-3 shrink-0 text-muted-foreground" />
              <span className="min-w-0 flex-1 truncate">{f.title}</span>
              {!f.drive && (
                <span className="shrink-0 text-[10px] text-muted-foreground">stored, not in Drive</span>
              )}
              <button
                type="button"
                onClick={() => onRemove(f.document_id)}
                aria-label={`Remove ${f.title}`}
                className="shrink-0 text-muted-foreground hover:text-foreground"
              >
                <X className="h-3 w-3" />
              </button>
            </li>
          ))}
        </ul>
      )}

      <div className="space-y-1.5">
        <Label htmlFor="inc-links" className="text-xs">
          Or links, one per line
        </Label>
        <Textarea
          id="inc-links"
          rows={2}
          value={links}
          onChange={(e) => onLinksChange(e.target.value)}
          placeholder="For evidence that already lives somewhere else"
        />
      </div>
    </div>
  )
}
